<template>
    <section id="signup-form">
        <div v-if="scope == 'step-1'" key="step-1">
            <form @submit.prevent="continueToStep2()" data-vv-scope="step-1">
                <input
                    type="hidden"
                    name="action"
                    value="users/save-user"
                />
                <p class="note">Note: your firm must have an account before you can register.</p>
                <div class="grid -col2 -form">
                    <div class="grid__cell">
                        <div class="text">
                            <label for="firstName">First Name <span class="required">*</span></label>
                            <input
                                data-vv-as="First Name"
                                aria-label="First Name"
                                title="First Name"
                                v-validate="rules['firstName']"
                                v-model="firstName"
                                type="text"
                                name="firstName"
                            />
                            <p
                                class="messaging -error -subtle"
                                v-show="errors.has('step-1.firstName')"
                                v-text="errors.first('step-1.firstName')"
                            ></p>
                        </div>
                    </div>
                    <div class="grid__cell">
                        <label for="lastName">Last Name <span class="required">*</span></label>
                        <div class="text">
                            <input
                                data-vv-as="Last Name"
                                aria-label="Last Name"
                                title="Last Name"
                                v-validate="rules['lastName']"
                                v-model="lastName"
                                type="text"
                                name="lastName"
                            />
                            <p
                                class="messaging -error -subtle"
                                v-show="errors.has('step-1.lastName')"
                                v-text="errors.first('step-1.lastName')"
                            ></p>
                        </div>
                    </div>
                </div>
                <div class="text">
                    <label for="corporateEmail">Corporate Email <span class="required">*</span></label>
                    <input
                        data-vv-as="Corporate Email"
                        aria-label="Corporate Email"
                        v-validate="rules['email']"
                        v-model="email"
                        type="text"
                        name="email"
                    />
                    <p
                        class="messaging -error -subtle"
                        v-show="errors.has('step-1.email')"
                        v-text="errors.first('step-1.email')"
                    ></p>
                </div>
                <input type="hidden" name="showPage" value="2">
                <div class="grid -col2 -action -vAlignCenter">
                    <div class="grid__cell">
                        Already have an account?
                        <a class="link -underline -dark -login" href="/login">Login</a>
                    </div>
                    <div v-if="errorMessage">
                        <p class="messaging -error -subtle" v-text="errorMessage"></p>
                    </div>
                    <div class="grid__cell -button">
                        <button class="button" type="submit" name="signup">Next</button>
                    </div>
                </div>
                <input type="hidden" :name="csrf['name']" :value="csrf['token']" />
                <input type="hidden" :name="redirect" :value="redirect" />
            </form>
        </div>
        <div v-else key="step-2">
            <form @submit.prevent="createAccount()" data-vv-scope="step-2">
                <div class="grid -col2 -form">
                    <div class="grid__cell ">
                        <div class="text">
                            <label for="password">Password <span class="required">*</span></label>
                            <input
                                data-vv-delay="5000"
                                data-vv-as="Password"
                                v-validate="rules['password']"
                                v-model="password"
                                type="password"
                                name="password"
                                title="Password"
                            />
                            <p
                                class="messaging -error -subtle"
                                v-show="errors.has('step-2.password')"
                                v-text="errors.first('step-2.password')"
                            ></p>
                        </div>
                    </div>
                    <div class="grid__cell">
                        <div class="text">
                            <label for="password">Confirm Password <span class="required">*</span></label>
                            <input
                                data-vv-delay="2000"
                                data-vv-as="Password Confirmation"
                                v-validate="rules['password_confirmation']"
                                v-model="password_confirmation"
                                type="password"
                                name="password_confirmation"
                                title="Password Confirmation"
                            />
                            <p
                                class="messaging -error -subtle"
                                v-show="errors.has('step-2.password_confirmation')"
                                v-text="errors.first('step-2.password_confirmation')"
                            ></p>
                        </div>
                    </div>
                </div>
                <div class="grid -col2 -form">
                    <div class="grid__cell">
                        <div class="select">
                            <label for="company">Company <span class="required">*</span></label>
                            <input
                                data-vv-as="Company"
                                aria-label="Company"
                                title="Company"
                                v-validate="rules['memberCompany']"
                                v-model="memberCompany"
                                id="memberCompany"
                                type="text"
                                name="memberCompany"
                            />
                            <p
                                class="messaging -error -subtle"
                                v-show="errors.has('step-2.memberCompany')"
                                v-text="errors.first('step-2.memberCompany')"
                            ></p>
                        </div>
                    </div>
                    <div class="grid__cell">
                        <div class="text">
                            <label for="jobTitle">Job Title <span class="required">*</span></label>
                            <input
                                data-vv-as="Job Title"
                                aria-label="Job Title"
                                title="Job Title"
                                v-validate="rules['memberTitle']"
                                v-model="memberTitle"
                                type="text"
                                name="memberTitle"
                            />
                            <p
                                class="messaging -error -subtle"
                                v-show="errors.has('step-2.memberTitle')"
                                v-text="errors.first('step-2.memberTitle')"
                            ></p>
                        </div>
                    </div>
                </div>
                <div>
                    <div class="select">
                        <label for="country">Country <span class="required">*</span></label>
                        <v-select
                            class="select__options"
                            data-vv-as="Country"
                            v-model="country"
                            name="country"
                            title="Country"
                            v-validate:country="rules['country']"
                            :transition="null"
                            :options="countryOptions"
                        ></v-select>
                        <p
                            class="messaging -error -subtle"
                            v-show="errors.has('step-2.country')"
                            v-text="errors.first('step-2.country')"
                        ></p>
                    </div>
                </div>
                <div>
                    <div class="select">
                        <label for="timezone">Time Zone <span class="required">*</span></label>
                        <v-select
                            class="select__options"
                            data-vv-as="Time Zone"
                            v-model="timezone"
                            name="timezone"
                            title="Timezone"
                            v-validate:timezone="rules['timezone']"
                            :transition="null"
                            :options="timezoneOptions"
                        ></v-select>
                        <p
                            class="messaging -error -subtle"
                            v-show="errors.has('step-2.timezone')"
                            v-text="errors.first('step-2.timezone')"
                        ></p>
                    </div>
                </div>
                <div>
                    <div class="text">
                        <label for="phone">Phone Number <span class="required">*</span></label>
                        <input
                            data-vv-as="Phone Number"
                            aria-label="Phone Number"
                            title="Phone Number"
                            v-validate="rules['phone']"
                            v-model="phone"
                            type="text"
                            name="phone"
                        />
                    </div>
                    <p
                        class="messaging -error -subtle"
                        v-show="errors.has('step-2.phone')"
                        v-text="errors.first('step-2.phone')"
                    ></p>
                </div>

                <div class="checkboxes">
                    <div class="checkbox">
                        <input
                            data-vv-as="Agree To Terms"
                            title="Agree To Terms"
                            data-vv-name="agreeToTerms"
                            id="agreeToTerms"
                            name="fields[memberAgreedToTerms]"
                            v-model="agreeToTerms"
                            v-validate="rules['agreeToTerms']"
                            type="checkbox"
                            value="1"
                            class="checkbox__input"
                        />
                        <label for="agreeToTerms" class="checkbox__label">
                            <span class="required">* You must check this box to continue.</span><br/>
                            By checking this box, you consent to receive emails from Cutter Associates. You may opt out
                            of these emails at any time. By checking this box, you also certify that you are an employee
                            or staff contractor in a staff augmentation role of the above-named firm, and that you agree
                            to abide by the terms of the firm's Cutter Membership Agreement, which prohibits you from
                            sharing access to any Cutter research and virtual or in-person events with any vendor,
                            contractor, consultant or third party provider of or to your firm. For further information,
                            please consult the <a href="/privacy" target="_blank">Privacy Policy</a> and
                            <a href="/terms-of-use" target="_blank">Terms of Use Policy</a>.
                        </label>
                    </div>
                </div>
                <div>
                    <p
                        class="messaging -error -subtle"
                        v-show="errors.has('step-2.agreeToTerms')"
                        v-text="errors.first('step-2.agreeToTerms')"
                    ></p>
                </div>
                <div v-if="errorMessage">
                    <p class="messaging -error -subtle" v-text="errorMessage"></p>
                </div>
                <div class="grid -col2 -action -vAlignCenter">
                    <div class="grid__cell">
                        <a class="link -underline" href="/register">Back</a>
                    </div>
                    <div class="grid__cell -button">
                        <button class="button" type="submit">Complete</button>
                    </div>
                </div>
            </form>
        </div>
    </section>
</template>

<script>
import locationData from "./location-data.js";
import {Validator} from "vee-validate";
import axios from "axios";
import VueScrollTo from "vue-scrollto";
import moment from "moment-timezone";
import {stringify} from 'qs';
import VueSanitize from "vue-sanitize";

export default {
    data() {
        return {
            scope: "step-1",
            error: "",
            processing: false,
            progressClass: false,

            firstName: null,
            lastName: null,
            email: null,

            password: null,
            password_confirmation: null,

            memberCompany: null,
            memberTitle: null,
            country: null,
            timezone: null,
            phone: null,

            agreeToTerms: null,

            timezoneOptions: [],
        };
    },

    computed: {
        rules() {
            return {
                firstName: {
                    required: true,
                    containsLetter: true,
                    max: 30,
                },
                lastName: {
                    required: true,
                    containsLetter: true,
                    max: 50,
                },
                email: {
                    required: true,
                    email: true,
                },
                password: {
                    required: true,
                    min: 8,
                    max: 64,
                },
                password_confirmation: {
                    required: true,
                    confirm: "password",
                },
                memberCompany: {
                    required: true,
                    containsLetter: true,
                    max: 50,
                },
                memberTitle: {
                    required: true,
                    containsLetter: true,
                    max: 100,
                },
                country: {
                    required: true,
                },
                timezone: {
                    required: true,
                },
                phone: {
                    required: true,
                    regex: /[\d().+-]/,
                    max: 20,
                },
                agreeToTerms: {
                    required: true,
                },
            };
        },

        csrf() {
            return {
                //name: window.tokenName,
                value: window.token,
            };
        },

        redirect() {
            return window.redirect;
        },

        countryOptions() {
            return locationData.countries;
        },

        hasErrors() {
            return this.errors.items.length > 0 ? true : false;
        },

        errorMessage() {
            if (this.error) {
                return this.error;
            } else if (this.hasErrors) {
                return "Please correct the invalid fields above.";
            }
        },


    },

    mounted() {
        this.$validator.extend("lettersWithBasicPunc", {
            getMessage: (field) =>
                `The ${field} field must only use letters, dashes, dots, commas, apostrophes, ampersands and parenthesis`,
            validate: (value) => /^[a-z\-\.\,&,()\s]+$/i.test(value),
        });

        this.$validator.extend("containsLetter", {
            getMessage: (field) => `The ${field} field must have at least 1 letter`,
            validate: (value) => /[a-z]/i.test(value),
        });

        this.$validator.extend("confirm", {
            // custom confirmation rule. vee validates built-in rule was not working.
            getMessage: (field) => `The ${field} does not match`,
            validate: (value, args) => this[args[0]] === value,
        });

        axios.get("/actions/auth/api/timezones").then((r) => {
            this.timezoneOptions = r.data;
        });

    },


    methods: {
        continueToStep2() {
            this.$validator.validateAll("step-1").then((result) => {
                // validation failed, return
                if (result === false) {
                    return;
                }

                axios
                    .get("/actions/auth/api/check-email", {
                        params: {email: this.email},
                    })
                    .then((r) => {
                        if (r.data == 1) {
                            this.error = "That email is already registered."
                        } else {

                            this.error = "";
                            let data = {
                                ...this.$data
                            };

                            //pass CSRF to POST data
                            data[this.csrf.name] = this.csrf.value;
                            data['redirect'] = this.redirect;

                            // post data to contact handler
                            //axios.post("/actions/signup-module/signup/signup-contact", data);

                            this.progressClass = true;

                            this.scope = "step-2";

                            VueScrollTo.scrollTo("#signup-form", 1000, {offset: -160});
                        }
                    });

            });
        },

        createAccount() {
            // if processing, don't allow re-submit
            if (this.processing) {
                return;
            }

            this.$validator.validateAll("step-2").then((result) => {

                this.error = "";

                // validation failed, return
                if (result === false) {
                    return;
                }

                let data = {
                    "firstName": this.$sanitize(this.firstName),
                    "lastName": this.$sanitize(this.lastName),
                    "email": this.email,
                    "password": this.password,
                    "fields[memberCompany]": this.$sanitize(this.memberCompany),
                    "fields[memberTitle]": this.$sanitize(this.memberTitle),
                    "fields[memberCountry]": this.country.value,
                    "fields[memberTimeZone]": this.timezone.value,
                    "fields[memberPhoneNumber]": this.phone,
                }

                if (this.agreeToTerms) {
                    data["fields[memberAgreedToTerms]"] = 1;
                    data["fields[memberCommunications]"] = 1;
                    data["fields[agreedToCurrentTermsAndConditions]"] = 1;
                }

                console.log(moment().format('MM/DD/YYYY'));
                data["fields[accountAnniversary]"] = moment().format('MM/DD/YYYY');

                //pass CSRF to POST data
                data["CRAFT_CSRF_TOKEN"] = this.csrf.value;
                data["action"] = "users/save-user";
                data["redirect"] = this.redirect;

                axios
                    .post("/", stringify(data), {
                        headers: {"X-CSRF-Token": window.token},
                    })
                    .then((response) => {
                        if (response.data.errors) {
                            for (var key in response.data.errors) {
                                this.error += response.data.errors[key] + "\n";
                            }
                        } else {
                            window.location = "members/dashboard";
                        }
                    })
                    .catch((error) => {
                        this.processing = false;
                        console.log("Error!");
                        if (error.response) {
                            console.log("yep, here is the error");
                            this.error = error.response.data.error;
                        } else {
                            this.error =
                                "An unknown error occurred - The account was not created. Please try again.";
                        }
                    });

            });
        },

    },
}
</script>
